// Shared utility functions used across multiple modules
import { t, getDocumentLanguage } from './i18n.ts';

export const JWT_KEY = 'smartpluvia-jwt';
export const USER_KEY = 'smartpluvia-user';
const TOAST_DURATION_MS = 5000;

/**
 * Get the stored JWT token from localStorage.
 */
/**
 * Deprecated as of #1494. The SPA no longer stores the access JWT in
 * localStorage — auth rides on the HttpOnly USER_JWT_COOKIE via
 * `credentials: 'include'`. Returns null always; callers should use
 * `isAuthenticated()` from `auth.ts` for boolean checks and
 * `authenticatedFetch` (which auto-includes credentials) for API calls.
 */
export function getToken(): string | null {
  return null;
}

/**
 * Returns true when the target is a form control or contentEditable — used to
 * avoid hijacking typing in inputs when binding global keyboard shortcuts.
 */
export function isEditableElement(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

/**
 * Escape HTML special characters to prevent XSS.
 * Covers quotes too, so escaped values are safe in attribute position
 * (the textContent/innerHTML trick leaves " and ' unescaped).
 */
const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

export function escapeHtml(str: string | null | undefined): string {
  if (!str) return '';
  return String(str).replace(/[&<>"']/g, (ch) => HTML_ESCAPES[ch]);
}

/**
 * Whitelist a CSS colour before it is interpolated into a `style="…"`
 * attribute. Catalog colours reach attribute position raw, so a value
 * carrying a quote breaks out of the attribute; the function forms
 * restrict the inner charset because `[^)]+` let `"`/`>` through.
 * Anything unrecognised yields '' so callers can drop the declaration.
 */
const CSS_COLOR = /^#[0-9a-fA-F]{3,8}$|^[a-zA-Z]+$|^rgba?\([\d\s.,%/]+\)$|^hsla?\([\d\s.,%/]+\)$/;

export function sanitizeColor(color: string | null | undefined): string {
  if (!color) return '';
  return CSS_COLOR.test(color) ? color : '';
}

/**
 * Show a toast notification that auto-hides after 5 seconds.
 */
export function showToast(message: string, type: 'error' | 'warning' | 'success' = 'error'): void {
  const existing = document.querySelector('.toast-notification');
  if (existing) existing.remove();

  const el = document.createElement('div');
  el.className = `toast-notification ${type}`;
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  el.textContent = message;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), TOAST_DURATION_MS);
}

/**
 * Format a date string as relative time (e.g., "5 min ago"). Past 30 days it
 * prints the date, in the page's language rather than the browser's.
 */
export function timeAgo(dateStr: string | null | undefined, translator?: (key: string) => string): string {
  if (!dateStr) return '\u2014';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const seconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (translator) {
    if (seconds < 60) return translator('comments.justNow');
    if (minutes < 60) return translator('comments.minutesAgo').replace('{n}', String(minutes));
    if (hours < 24) return translator('comments.hoursAgo').replace('{n}', String(hours));
    if (days < 30) return translator('comments.daysAgo').replace('{n}', String(days));
    return date.toLocaleDateString(getDocumentLanguage());
  }

  if (minutes < 1) return t('timeAgo.justNow');
  if (minutes < 60) return t('timeAgo.minutesAgo', { n: minutes });
  if (hours < 24) return t('timeAgo.hoursAgo', { n: hours });
  if (days < 30) return t('timeAgo.daysAgo', { n: days });
  return date.toLocaleDateString(getDocumentLanguage());
}

interface FetchOptions extends Omit<RequestInit, 'body'> {
  body?: BodyInit | Record<string, unknown> | null;
  headers?: Record<string, string>;
  /** Skip automatic token refresh on 401 (used internally to prevent loops) */
  _skipRefresh?: boolean;
}

/**
 * Build request headers + body for an authenticated API call. As of #1494
 * the SPA no longer attaches an `Authorization: Bearer …` header — auth
 * rides on the HttpOnly USER_JWT_COOKIE that the browser auto-sends when
 * we request with `credentials: 'include'`. JWT in localStorage was the
 * exfiltration target for any XSS on the origin, so we stopped storing
 * it client-side. The server still accepts the Authorization header for
 * back-compat with external clients (curl, scripts).
 */
function buildRequest(options: FetchOptions): { headers: Record<string, string>; body: BodyInit | undefined } {
  const headers: Record<string, string> = { ...options.headers };
  let body = options.body;
  if (body && typeof body === 'object' && !(body instanceof FormData) && !(body instanceof Blob) && !(body instanceof ArrayBuffer) && !(body instanceof URLSearchParams)) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(body);
  }
  return { headers, body: body as BodyInit | undefined };
}

/**
 * Make an authenticated API request. The browser attaches the
 * USER_JWT_COOKIE via `credentials: 'include'`; on 401 we kick a
 * refresh flow (which itself reads the REFRESH_TOKEN_COOKIE) and retry.
 */
export async function authenticatedFetch(url: string, options: FetchOptions = {}): Promise<Response> {
  const { headers, body } = buildRequest(options);
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers,
    body,
  });

  if (response.status === 401 && !options._skipRefresh) {
    // Lazy import to avoid circular dependency
    const { refreshAccessToken } = await import('./auth.ts');
    const newToken = await refreshAccessToken();
    if (newToken) {
      // Retry; the refreshed JWT is in the cookie jar already.
      return authenticatedFetch(url, { ...options, _skipRefresh: true });
    }
  }

  return response;
}
